import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Plane, Package, MapPin, Calendar } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const EmiratesVTOLArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <Button 
          variant="ghost" 
          onClick={() => window.history.back()}
          className="mb-6 hover:bg-gray-100"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Industry Highlights
        </Button>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-blue-500 text-white">
                <Plane className="h-3 w-3 mr-1" />
                Innovation
              </Badge>
              <span className="text-gray-500 text-sm">November 21, 2025</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Emirates SkyCargo Partners with LODD for Revolutionary VTOL Cargo Solutions
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              Emirates SkyCargo and LODD Autonomous forge strategic alliance to develop next-generation 
              VTOL cargo aircraft, promising to transform regional logistics and delivery capabilities.
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://media.assettype.com/gulfnews%2Fimport%2F2011%2F1%2F23%2F1_16a07f7eb24.751149_3812657392_16a07f7eb24_large.jpg?w=480&auto=format%2Ccompress&fit=max" 
              alt="VTOL cargo aircraft and advanced drone technology" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Package className="h-5 w-5 mr-2 text-blue-600" />
                  Payload
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">250kg</div>
                <p className="text-sm text-gray-600">Maximum capacity</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-green-600" />
                  Range
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">700km</div>
                <p className="text-sm text-gray-600">Maximum distance</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-purple-600" />
                  Target
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600">2027</div>
                <p className="text-sm text-gray-600">Commercial deployment</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Strategic Partnership Overview</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              The Memorandum of Understanding signed between Emirates SkyCargo and LODD Autonomous at the 
              Dubai Air Show represents a significant milestone in aviation logistics innovation. This 
              partnership combines Emirates' global logistics expertise with LODD's cutting-edge VTOL 
              technology to create next-generation air cargo solutions.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">The Hili Aircraft</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              At the heart of this partnership is the Hili, LODD Autonomous's revolutionary hybrid heavy-lift 
              cargo aircraft. Key specifications include:
            </p>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Hili Technical Specifications:</h4>
              <ul className="space-y-2">
                <li><strong>Payload Capacity:</strong> Up to 250 kilograms</li>
                <li><strong>Range:</strong> Up to 700 kilometers</li>
                <li><strong>Design:</strong> Unmanned, hybrid heavy-lift configuration</li>
                <li><strong>Manufacturing:</strong> Designed and built entirely in Abu Dhabi</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Successful Test Flight</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              On November 13, 2025, during Abu Dhabi Autonomous Week, LODD Autonomous achieved a major 
              milestone by conducting the first test flight of the Hili aircraft at Emirates Falcon Aviation 
              in Al Ain. This successful demonstration, witnessed by Zayed bin Mohamed bin Zayed, validated 
              the aircraft's design and operational capabilities.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Development Program</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              The collaboration will focus on comprehensive validation and development activities:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>Feasibility Studies:</strong> Analyzing integration with Emirates' global network</li>
              <li><strong>Regulatory Engagement:</strong> Working with aviation authorities for certification</li>
              <li><strong>Live Demonstrations:</strong> Real-world testing in operational environments</li>
              <li><strong>Design Optimization:</strong> Refining aircraft based on operational insights</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Timeline and Objectives</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The program will run until the end of 2027, gathering crucial insights to inform design 
              and development processes. The ultimate goal is commercial deployment in both regional 
              and global markets, leveraging Emirates SkyCargo's extensive network and operational expertise.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Industry Leadership Perspectives</h3>
            <div className="space-y-4 mb-6">
              <blockquote className="border-l-4 border-blue-500 pl-6 italic text-gray-700">
                <p className="mb-2">
                  "This partnership with LODD is a reflection of our commitment to introduce innovative 
                  products that solve our customer's transportation challenges. Emerging technologies will 
                  form the foundation of the next era of logistics."
                </p>
                <footer className="text-sm font-semibold">
                  — Badr Abbas, Emirates SkyCargo Divisional Senior Vice President
                </footer>
              </blockquote>

              <blockquote className="border-l-4 border-green-500 pl-6 italic text-gray-700">
                <p className="mb-2">
                  "Our collaboration with Emirates SkyCargo blends LODD Autonomous's frontier technologies 
                  with the country's enduring commitment to safe, scalable, and sustainable logistics."
                </p>
                <footer className="text-sm font-semibold">
                  — Rashid Mattar Al Manai, CEO of LODD Autonomous
                </footer>
              </blockquote>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">UAE's Strategic Vision</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              This partnership aligns with the UAE's broader vision of harnessing innovation to advance 
              everyday life. The collaboration represents a convergence of cutting-edge technology with 
              practical logistics applications, positioning the UAE as a global leader in autonomous 
              cargo solutions.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Market Impact Potential</h3>
            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Expected Benefits:</h4>
              <ul className="space-y-2">
                <li><strong>Expanded Reach:</strong> Access to previously difficult-to-serve locations</li>
                <li><strong>Reduced Delivery Times:</strong> Direct point-to-point cargo transport</li>
                <li><strong>Enhanced Flexibility:</strong> VTOL capabilities for diverse landing sites</li>
                <li><strong>Sustainable Operations:</strong> Hybrid technology for reduced environmental impact</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Broader Industry Connections</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              LODD Autonomous has also signed a similar MoU with Etihad Cargo, indicating strong industry 
              interest in VTOL cargo solutions. This multi-partner approach suggests a coordinated effort 
              to establish the UAE as a hub for next-generation logistics technology.
            </p>

            <div className="bg-gray-100 p-6 rounded-lg">
              <h4 className="font-semibold mb-2">Source Information:</h4>
              <p className="text-sm text-gray-600">
                Based on official announcements from the Dubai Air Show and LODD Autonomous test flight coverage. 
                <a href="https://www.aerotime.aero/articles/emirates-skycargo-vtol-cargo-lodd" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline ml-1">
                  View original source
                </a>
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default EmiratesVTOLArticle;